import React, { useReducer, useState, forwardRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Checkbox as PrestyledCheckbox } from '@streamr/streamr-layout'
import { REGULAR, TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import MigrationModal from './MigrationModal'
import { Alert } from './MigrationModal.styled'
import EthersProvider, { useEthers } from './EthersProvider'
import MigrateButton from './MigrateButton'
import MigrationProgress from './MigrationProgress'
import services from './services'
import AnotherDimension from '~shared/AnotherDimension'
import { Headline, Paragraph } from '~/components/DataFund/Hero'
import Markdown from '~shared/Md'
import Layout from '~shared/Layout'
import Link from '~shared/Link'
import Spacer from '~shared/Spacer'
import ProgressCounter from '~shared/ProgressCounter'
import useIsMounted from '~hooks/useIsMounted'

const { wallets, markets, exchanges } = services

const Container = styled.div``

const Subheading = styled.h2``

const TopSubheading = styled(Subheading)``

const ExternalServicesWrapper = styled.div``

const UpdatedAt = styled.div``

const VideoWrapper = styled.div``

const Video = styled.iframe``

const CheckboxLabel = styled.label``

const ConditionsWrapper = styled.div``

const Checkbox = styled(PrestyledCheckbox)``

const Services = styled.div``

const Service = styled.div`
    color: #323232;

    ${({ heading }) => !heading && css`
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.05);

        > div + div {
            color: #0324ff;
        }
    `}
`

const ServiceListAttrs = ({ entityColumn, statusColumn = 'Status', items = [] }) => ({
    children: (
        <>
            <Service heading $appear>
                <div>
                    <strong>{entityColumn}</strong>
                </div>
                <div>
                    <strong>{statusColumn}</strong>
                </div>
            </Service>
            {items.length ? (
                items.map(({ name, pair, status = pair, url }) => (
                    <Service key={`${name}-${status}`} $appear>
                        <div>{name}</div>
                        <div>
                            {url ? (
                                <Link href={url} newTab>
                                    {status}
                                </Link>
                            ) : (
                                <span>
                                    {status}
                                </span>
                            )}
                        </div>
                    </Service>
                ))
            ) : (
                <Service heading>
                    <div>No items.</div>
                    <div />
                </Service>
            )}
        </>
    ),
})

const ServiceList = styled.div.attrs(ServiceListAttrs)``

const UnstyledNoEthereumAlert = ({ className }) => {
    const { metamaskProvider } = useEthers()

    const isMounted = useIsMounted()

    const [ok, setOk] = useState(true)

    useEffect(() => {
        // We delay showing the status to improve the visual effects. There's no extra
        // logic behind it.
        const tID = setTimeout(() => {
            if (isMounted()) {
                setOk(!!metamaskProvider)
            }
        }, 2000)

        return () => {
            clearTimeout(tID)
        }
    }, [isMounted, metamaskProvider])

    return !ok ? (
        <Spacer className={className} head="32px,," tail>
            <Alert $appearIntersection $appearDirection="none">
                Please open this page in a Web3 enabled browser (eg Chrome) to use the migration tool
            </Alert>
        </Spacer>
    ) : (
        <Spacer pad head="48px,,64px" />
    )
}

const NoEthereumAlert = styled(UnstyledNoEthereumAlert)`
    background-color: #fdf8f8;
    border-radius: 4px;
    padding: 0 32px;

    ${Alert} {
        height: 64px;
        justify-content: flex-start;
    }

    ${Alert} span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`

const Cta = forwardRef(({ onClick, style }, ref) => {
    const { metamaskProvider } = useEthers()

    const [termsAccepted, toggleTerms] = useReducer((x) => !x, false)

    const disabled = !termsAccepted || !metamaskProvider

    return (
        <div style={style} ref={ref}>
            <ConditionsWrapper>
                <CheckboxLabel>
                    <Checkbox
                        value={termsAccepted}
                        onChange={toggleTerms}
                    />
                    <span>
                        <Markdown
                            inline
                            renderers={{
                                link: Link,
                            }}
                        >
                            I have read and agree to the
                            [terms and conditions](https://streamr-public.s3.amazonaws.com/migration-terms-and-conditions.pdf)
                        </Markdown>
                    </span>
                </CheckboxLabel>
                <MigrateButton disabled={disabled} onClick={onClick} />
            </ConditionsWrapper>
        </div>
    )
})

const UnstyledTokenMigration = (props) => {
    const [modalOpen, setModalOpen] = useState(false)

    const [migrationProgressCache, touchMigrationProgressCache] = useReducer((x) => x + 1, 0)

    const open = () => {
        setModalOpen(true)
    }

    const close = () => {
        touchMigrationProgressCache()
        setModalOpen(false)
    }

    return (
        <Layout {...props}>
            <EthersProvider>
                <AnotherDimension>
                    <MigrationModal close={close} shown={!!modalOpen} />
                </AnotherDimension>
                <Container>
                    <Spacer head="64px,104px,232px" tail="32px,,">
                        <Headline as="h1" $appear>
                            Token Migration
                        </Headline>
                    </Spacer>
                    <Paragraph $appear>
                        <Markdown
                            inline
                            renderers={{
                                link: Link,
                            }}
                        >
                            With the passing of **SIP-1**, the Streamr community has decided to upgrade the
                            DATA token to allow the token supply to be increased based on governance decisions,
                            and modernise some of the token features. Both the old and new tokens will coexist
                            during the transition period. The old token has been renamed to XDATA, and the new token
                            uses the symbol DATA. We are in contact with major exchanges and the team is
                            supporting them through the migration process.
                            [Learn more](https://blog.streamr.network/p/b9794c09-202d-4bd9-b9b2-34e5d8f604e8/)
                            about the migration.
                        </Markdown>
                    </Paragraph>
                    <Spacer head="48px,64px," tail="64px,88px,144px">
                        <MigrationProgress cacheKey={migrationProgressCache} $appear />
                    </Spacer>
                    <TopSubheading $appear>Migrate now</TopSubheading>
                    <Paragraph $appear>
                        <Markdown
                            inline
                            renderers={{
                                link: Link,
                            }}
                        >
                            You can use the app below to migrate your XDATA to the new DATA. Please watch
                            the video first to ensure you understand the process. Connect your wallet to get
                            started, and if you still need help, come and ask questions in the
                            dedicated [Discord channel](https://discord.gg/gZAm8P7hK8).
                        </Markdown>
                    </Paragraph>
                    <NoEthereumAlert />
                    <Spacer tail="72px,88px,168px">
                        <VideoWrapper $appearDirection="none">
                            <Video
                                width="560"
                                height="315"
                                src="https://www.youtube-nocookie.com/embed/cNb7wEezdrU"
                                title="DATA Token Migration Tutorial"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen
                            />
                        </VideoWrapper>
                        <Cta onClick={open} $appear />
                    </Spacer>
                </Container>
                <ExternalServicesWrapper>
                    <Spacer head="88px,104px,168px" tail pad>
                        <Container>
                            <TopSubheading $appear>
                                External services status
                            </TopSubheading>
                            <UpdatedAt $appear>
                                <Markdown
                                    inline
                                    renderers={{
                                        link: Link,
                                    }}
                                >
                                    **Updated 27th September 2021** Are we missing a service? Drop us an email
                                    [support@streamr.network](mailto:support@streamr.network)
                                </Markdown>
                            </UpdatedAt>
                            <Services>
                                <div>
                                    <Subheading $appear>Wallets</Subheading>
                                    <p $appear>DATA and XDATA should display correctly</p>
                                </div>
                                <ServiceList entityColumn="Wallet" items={wallets} />
                            </Services>
                            <Services>
                                <div>
                                    <Subheading $appear>DATA Markets</Subheading>
                                    <p $appear>Confirmed and active DATA trading&nbsp;pairs</p>
                                </div>
                                <ServiceList entityColumn="Exchange" statusColumn="Trading pairs" items={markets} />
                            </Services>
                            <Services>
                                <div>
                                    <Subheading $appear>Exchanges</Subheading>
                                    <p $appear>Exchanges migration status</p>
                                </div>
                                <ServiceList entityColumn="Exchange" items={exchanges} />
                            </Services>
                        </Container>
                    </Spacer>
                </ExternalServicesWrapper>
            </EthersProvider>
        </Layout>
    )
}

const TokenMigration = styled(UnstyledTokenMigration)`
    color: #0C009A;

    ${MigrateButton}[disabled] {
        opacity: 0.5;
    }

    ${Container} {
        margin: 0 auto;
        padding: 0 32px;
        max-width: 496px;
    }

    ${ProgressCounter.Hub} {
        line-height: 24px;
        /* -7px compensates for Hub's line height of 24px (10px font + 14px extra). */
        margin: 16px 0 -7px;
    }

    ${ProgressCounter.HubItem} a {
        margin-right: 0.75em;
    }

    ${MigrationProgress} ${Link} {
        color: #0421ff !important;
    }

    ${Subheading} {
        font-weight: ${REGULAR};
        font-size: 28px;
        line-height: 40px;
        margin: 0;
    }

    ${TopSubheading} {
        margin: 0 0 48px;
    }

    ${ExternalServicesWrapper} {
        background-color: #fafafa;
    }

    ${UpdatedAt} {
        background-color: #f5f5f5;
        color: #323232;
        border-radius: 8px;
        font-size: 14px;
        line-height: 24px;
        padding: 24px 16px;
    }

    ${UpdatedAt} strong {
        display: block;
    }

    ${VideoWrapper} {
        border-radius: 8px;
        overflow: hidden;
        padding-top: 56.25%;
        position: relative;
    }

    ${Video} {
        background-color: #f3f3f3;
        border: 0;
        display: block;
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }

    ${UpdatedAt} ${Link} {
        color: #0421ff !important;
    }

    ${CheckboxLabel} {
        color: #525252;
        display: flex;
        font-size: 14px;
        line-height: 24px;
    }

    ${CheckboxLabel} ${Link} {
        color: inherit !important;
        text-decoration: underline !important;
    }

    ${ConditionsWrapper} {
        background-color: #fcfcfc;
        border-radius: 8px;
        font-size: 14px;
        margin-top: 32px;
        padding: 32px;
    }

    ${Alert} + ${ConditionsWrapper} {
        margin-top: 0;
    }

    ${MigrateButton} {
        margin: 32px 0 0 32px;
    }

    ${Checkbox} {
        margin: 4px 16px 0 0;
    }

    ${Services} {
        margin-top: 112px;
    }

    ${Services} + ${Services} {
        border-top: 1px solid #e7e7e7;
        padding-top: 112px;
    }

    ${Services} > div:first-child {
        margin-bottom: 48px;
    }

    ${Service} {
        display: flex;
        font-size: 14px;
        line-height: 16px;
        padding: 24px;
    }

    ${Service} > div:first-child {
        flex-grow: 1;
    }

    ${Service} > div + div {
        width: 96px;
    }

    ${Service} + ${Service} {
        margin-top: 16px;
    }

    ${Service}:first-child + & {
        margin-top: 0;
    }

    @media (min-width: ${TABLET}px) {
        ${Container} {
            max-width: none;
            width: 496px;
        }

        ${ProgressCounter.Hub} {
            margin: 20px 0 -6px;
        }

        ${Subheading} {
            font-size: 32px;
        }

        ${ExternalServicesWrapper} ${Container} {
            max-width: 648px;
            width: auto;
        }

        ${UpdatedAt} strong {
            display: inline;
        }

        ${Services} {
            display: flex;
            margin-top: 136px;
        }

        ${Services} > div:first-child {
            flex-grow: 1;
            margin: 0;
        }

        ${Services} > div:first-child p {
            padding-right: 32px;
        }

        ${ServiceList} {
            width: 312px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        ${Container} {
            width: 736px;
        }

        ${ProgressCounter.Hub} {
            display: flex;
            line-height: 1em;
            margin: 40px 0 0;
        }

        ${ProgressCounter.HubItem} {
            flex: 0 0 50%;
        }

        ${Subheading} {
            font-size: 40px;
            line-height: 48px;
        }

        ${ExternalServicesWrapper} ${Container} {
            max-width: none;
            width: 736px;
        }

        ${UpdatedAt} {
            font-size: 16px;
            padding: 24px 32px;
        }

        ${ConditionsWrapper} {
            align-items: center;
            display: flex;
        }

        ${ConditionsWrapper} ${CheckboxLabel} {
            flex-grow: 1;
        }

        ${MigrateButton} {
            margin: 0;
        }

        ${ServiceList} {
            width: 344px;
        }
    }

`

export default TokenMigration
